import React, { useMemo } from "react";
import { useTranslation } from "react-i18next";
import { Icon } from "@/components/Icon";

type TaskPhase = "general" | "frontend" | "backend" | "database";

interface TaskPhaseTimelineProps {
    messages: Array<{ comment_type?: TaskPhase | null }>;
    activePhase: TaskPhase | "all";
    onSelectPhase: (phase: TaskPhase | "all") => void;
    onAdvance: () => void;
}

const phases: TaskPhase[] = ["general", "frontend", "backend", "database"];

export const TaskPhaseTimeline: React.FC<TaskPhaseTimelineProps> = ({
    messages,
    activePhase,
    onSelectPhase,
    onAdvance,
}) => {
    const { t } = useTranslation("global");
    const completedPhases = useMemo(
        () =>
            new Set(
                messages
                    .map((message) => message.comment_type)
                    .filter((phase): phase is TaskPhase => Boolean(phase))
            ),
        [messages]
    );
    const activeIndex = phases.indexOf(activePhase as TaskPhase);
    const canAdvance = activeIndex >= 0;
    const nextPhase = activeIndex >= 0 ? phases[activeIndex + 1] : undefined;

    return (
        <div className="mb-6 rounded-xl border border-slate-200 bg-white px-4 py-5 dark:border-white/10 dark:bg-zinc-900">
            <div className="flex items-start overflow-x-auto pb-1">
                {phases.map((phase, index) => {
                    const isCompleted = completedPhases.has(phase);
                    const isActive = activePhase === phase;

                    return (
                        <div key={phase} className="flex min-w-0 flex-1 items-start">
                            <button
                                type="button"
                                onClick={() => onSelectPhase(phase)}
                                className="group flex min-w-[92px] flex-1 flex-col items-center text-center"
                                aria-current={isActive ? "step" : undefined}
                            >
                                <span
                                    className={`flex h-8 w-8 items-center justify-center rounded-full border-2 text-xs font-bold transition-colors ${
                                        isActive
                                            ? "border-primary bg-primary text-white"
                                            : isCompleted
                                              ? "border-emerald-500 bg-emerald-50 text-emerald-600 dark:bg-emerald-950/30"
                                              : "border-slate-200 bg-slate-50 text-slate-400 group-hover:border-primary/50 group-hover:text-primary dark:border-white/10 dark:bg-zinc-800"
                                    }`}
                                >
                                    {isCompleted ? (
                                        <Icon value="check" size={14} />
                                    ) : (
                                        index + 1
                                    )}
                                </span>
                                <span
                                    className={`mt-2 text-xs font-semibold ${isActive ? "text-primary" : "text-slate-600 dark:text-zinc-300"}`}
                                >
                                    {t(`tasks.comments.types.${phase}`)}
                                </span>
                                <span className="mt-1 text-[10px] text-slate-400 dark:text-zinc-500">
                                    {isCompleted
                                        ? t("tasks.comments.phase_reviewed")
                                        : t("tasks.comments.phase_pending")}
                                </span>
                            </button>

                            {index < phases.length - 1 && (
                                <span className="mt-4 h-px min-w-4 flex-1 bg-slate-200 dark:bg-white/10" />
                            )}
                        </div>
                    );
                })}
            </div>

            {activePhase !== "all" && (
                <div className="mt-5 flex justify-center">
                    <button
                        type="button"
                        onClick={onAdvance}
                        disabled={!canAdvance}
                        className="btn-primary inline-flex items-center gap-2 px-4 py-2 text-xs font-semibold disabled:cursor-not-allowed disabled:opacity-50"
                    >
                        {nextPhase
                            ? `${t("tasks.comments.advance_phase")}: ${t(`tasks.comments.types.${nextPhase}`)}`
                            : t("tasks.comments.complete_phase")}
                        <Icon value="arrowRight" size={14} />
                    </button>
                </div>
            )}
        </div>
    );
};
